import NiceModal from '@ebay/nice-modal-react';
import { useChannelTalk } from '@repo/channel-talk';
import { useUTM } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { useMount } from 'react-use';

import { applyPossibleQuery } from '@/__generated__/applyPossibleQuery.graphql';
import { useLookupResultRefundData } from '@/components/tax-refund/lookup/result/LookupResultContext';
import { IncomeNoticeModal } from '@/components/tax-refund/lookup/result/apply-possible/IncomeNoticeModal';
import { PreAccountInfo } from '@/components/tax-refund/lookup/result/apply-possible/PreAccountInfo';
import { RefundAgreementDrawer } from '@/components/tax-refund/lookup/result/apply-possible/template/v-20260211/RefundAgreementDrawer';
import { useApplyPossibleRefundData, useRefundConversionData } from '@/components/tax-refund/lookup/result/apply-possible/use-apply-possible-refund-data';
import { applyPossibleContentQuery } from '@/graphql/query/lookup/result/apply-possible';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { CURRENT_LOOKUP_RESULT_TERMPLATE_VERSION } from '@/lib/constants/variables';
import { useSnapshotData } from '@/lib/hooks/use-snapshot-data';

export const ApplyPossibleContent = () => {
  const router = useRouter();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { openChannelTalk } = useChannelTalk();
  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const { getConversionData } = useRefundConversionData();
  const { utmData } = useUTM();
  const { showDialog } = useDialog();
  const { saveSnapshotData } = useSnapshotData('lookup');
  const [PresentationalComponent, setPresentationalComponent] = useState<any>(null);

  const { refundId, businessType, refundStatus, refundAmount, tritxRefundInfo, displayName } = useLookupResultRefundData();
  const targetRefundId = refundId || tritxRefundInfo?.refundId || '';
  const data = useLazyLoadQuery(
    applyPossibleContentQuery,
    {
      refundId: targetRefundId,
      partnerId: utmData?.utmCampaign ?? ''
    },
    { fetchPolicy: 'store-or-network' }
  ) as applyPossibleQuery['response'];

  const { isVIP, lastLoginType, possibleApply, isAlreadyApplied, email, defaultFeeRate } = useApplyPossibleRefundData(data);

  const incomeRefundAmount = refundStatus === 'ApplyPossible' && refundAmount ? refundAmount || 0 : 0;
  const tritxRefundAmount = tritxRefundInfo?.refundStatus === 'ApplyPossible' && tritxRefundInfo?.refundAmount ? tritxRefundInfo.refundAmount : 0;
  const isIncomeTaxIncluded = refundStatus === 'ApplyPossible' && refundAmount > 0;
  const isTritxTaxIncluded = tritxRefundInfo?.refundStatus === 'ApplyPossible' && tritxRefundAmount > 0;

  const displayData = {
    refundId: targetRefundId,
    userName: displayName,
    businessType,
    incomeRefundAmount,
    tritxRefundAmount,
    isTritxTaxIncluded,
    isVIP,
    partnerDiscountInformation: data.refundPartner
  };

  const showAgreementDrawer = () => {
    const { refundType, refundGrade } = getConversionData(businessType, incomeRefundAmount, refundStatus, tritxRefundAmount, tritxRefundInfo?.refundStatus);

    sendClickEvent(`${businessType}-lookup_result_apply`, {
      grade: refundGrade,
      type: refundType
    });

    if (isAlreadyApplied) {
      showDialog({
        title: '이미 환급을 신청하셨네요!',
        description: (
          <div className={'flex flex-col gap-normal-small items-start text-left text-[14px] text-gray-700 leading-5 pb-3'}>
            <div className={'text-[15px]'}>결과는 신청했던 계정으로만 확인이 가능해요. 비즈넵 환급 서비스에서 아래 로그인 방법을 통해 확인해주세요.</div>
            <PreAccountInfo loginType={lastLoginType.toUpperCase()} email={email} />
          </div>
        ),
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              sendClickEvent(`${businessType}-lookup_result_error_sametin-ok`);
            }
          },
          secondary: {
            text: '문의하기',
            variant: 'tonal-secondary',
            onClick: () => {
              sendClickEvent(`${businessType}-lookup_result_error_sametin-cx`);
              openChannelTalk(SUPPORT_BOT_LINK.조회완료.일반);
            }
          }
        }
      });
      return;
    }

    if (possibleApply) {
      NiceModal.show(RefundAgreementDrawer, {
        feeRate: data.refundPartner?.feeRate,
        defaultFeeRate,
        handleSubmit,
        keepMounted: true
      });
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    sendClickEvent(`${businessType}-lookup_result_next`);
    if (isIncomeTaxIncluded) {
      sessionStorage.setItem('includeGlobalIncomeTax', 'true');
    }
    if (isTritxTaxIncluded) {
      sessionStorage.setItem('includeCapitalGainsTax', 'true');
    }

    saveSnapshotData(targetRefundId, displayData);
    router.push({ pathname: businessType === 'indis' ? COMMON_PATHS.위임장서명 : COMMON_PATHS.환급계좌입력, query: { type: businessType } });
    NiceModal.hide(RefundAgreementDrawer);
  };

  useEffect(() => {
    const isShowIncomeNoticeModal = sessionStorage.getItem('isPersonalTritx') && isIncomeTaxIncluded && businessType === 'indis';

    if (isShowIncomeNoticeModal) {
      NiceModal.show(IncomeNoticeModal, { keepMounted: true });
      sendViewEvent('indis-lookup_modal_discover-global-income', {
        type: incomeRefundAmount > 0 && tritxRefundAmount > 0 ? 'all' : 'only-income'
      });
    }
  }, [incomeRefundAmount, tritxRefundAmount, sendViewEvent, isIncomeTaxIncluded, businessType]);

  useMount(() => {
    const { totalAmount, incomeAmount, tritxAmount, refundType, refundGrade } = getConversionData(
      businessType,
      refundAmount,
      refundStatus,
      tritxRefundInfo?.refundAmount,
      tritxRefundInfo?.refundStatus
    );
    sendViewEvent(businessType === 'indis' ? 'indis-lookup_result' : 'corps-lookup-result', {
      amount: totalAmount,
      incomeAmount,
      tritxAmount,
      grade: refundGrade,
      type: refundType
    });
  });

  useEffect(() => {
    try {
      const Comp = require(`./template/${CURRENT_LOOKUP_RESULT_TERMPLATE_VERSION}/ApplyPossiblePresentationalContent`);
      setPresentationalComponent(() => Comp.ApplyPossiblePresentationalContent);
    } catch (e) {
      setPresentationalComponent(null);
    }
  }, []);

  return <>{PresentationalComponent && <PresentationalComponent {...displayData} isSubmitted={isSubmitted} onClickCta={showAgreementDrawer} />}</>;
};
